import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useActions } from 'hooks/useActions';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, InputField, Typography, color } from '@kis-technologies/kis-ui';
import { ActionContainer } from 'components/ActionContainer';
import { selectors as authSelectors, actions as authActions } from 'store/auth';
import { Admin } from 'store/admins';
import { ValidPasswordToolTip } from '../../components/ValidPasswordToolTip';
import { validationSchema } from 'views/UsersView/utils';
import { useStyles } from './styled';

const ResetPasswordView: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useParams() ?? {};

  const { updateAdmin, resetAction, resetPassword } = useActions({
    updateAdmin: authActions.updateCurrentAdmin,
    resetAction: authActions.resetAction,
    resetPassword: authActions.resetPassword
  });

  const isAuthenticated = useSelector(authSelectors.selectIsAuthenticated);
  const admin = useSelector(authSelectors.selectAdmin) as Admin;
  const error = useSelector(authSelectors.selectActionError);
  const updating = useSelector(authSelectors.selectActionPending);
  const updateSuccess = useSelector(authSelectors.selectActionSuccessful);

  const classes = useStyles();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [invalidMsg, setInvalidMsg] = useState<string | null>(null);
  const [showPasswordInfo, setPasswordInfoVisibility] = useState(false);

  const passwordsMismatch = !password || !!invalidMsg || password !== confirmPassword;

  useEffect(() => {
    if (!isAuthenticated && !token) navigate('/');
  }, []);

  // ON UPDATE OF ERROR - SET THE ERROR AS INVALID MESSAGE
  useEffect(() => {
    setInvalidMsg(error);
  }, [error]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  // PASSWORD VALIDATION
  const passwordValidationFn = async () => {
    // IF THERE WAS ERROR MESSAGE - SET IT TO INVALID MESSAGE
    if (passwordsMismatch) {
      setInvalidMsg('Passwords do not match');
    } else {
      let invalidMessage = null;

      // IF THE TRIMMED STRING VALUE HAS VALUE RUN VALIDATION AND SET INVALID MESSAGE
      if (password) {
        const result = await validationSchema.validate({ password }).catch((err) => err);
        invalidMessage = result && result.message;
      }

      setInvalidMsg(invalidMessage);
    }

    return null;
  };

  // INPUT CHANGE
  const onInputChange = (key: string, value: string) => {
    // IF INVALID MESSAGE EXISTS SET IT TO NULL
    if (invalidMsg) setInvalidMsg(null);

    if (key === 'password') {
      setPasswordInfoVisibility(true);
      setPassword(value);
    } else if (key === 'confirmPassword') {
      setConfirmPassword(value);
    }
  };

  // FORM INPUT BLUR
  const onInputBlur = async (key: string, value: string | null = null) => {
    if (key === 'password') {
      setPasswordInfoVisibility(false);
    }
    let invalidMessage = null;

    // IF THE TRIMMED STRING VALUE HAS VALUE RUN VALIDATION AND SET INVALID MESSAGE
    if (value && value.toString().trim().length) {
      const result = await validationSchema.validate({ [key]: value && value.toString().trim() }).catch((err) => err);
      invalidMessage = result && result.message;
    }

    setInvalidMsg(invalidMessage);
  };

  // FORM SUBMIT
  const onSaveClick = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (updateSuccess) {
      resetAction();
      navigate('/');
    } else if (token) {
      resetPassword({ token, password });
    } else {
      updateAdmin({ password });
    }
  };

  if (!isAuthenticated && !token) return null;

  return (
    <ActionContainer
      header="Change Password"
      buttonAction={onSaveClick}
      buttonDisabled={passwordsMismatch && !updateSuccess}
      isLoading={updating}
    >
      <Box>
        {!updateSuccess ? (
          <>
            {/* FIRST TIME LOGIN INSTRUCTIONS */}
            {!token && (
              <Box className={classes.instructionsBox}>
                <Typography>
                  Welcome {admin.name}! This appears to be your first time logging in. Please take the time to update
                  your password to one that is secure and easy to remember.
                </Typography>
              </Box>
            )}
            {/* PASSWORD INPUT */}
            <Box className={classes.inputFieldBox}>
              <InputField
                onChange={(e) => onInputChange('password', e.target.value)}
                error={!!invalidMsg}
                label="New Password"
                type="password"
                placeholder="New Password"
                onBlur={(e) => onInputBlur('password', e.target.value.trim())}
                onFocus={() => setPasswordInfoVisibility(true)}
                endIcon={<ValidPasswordToolTip showValidPasswordInfo={showPasswordInfo} value={password} />}
                size="small"
                sx={{ width: { xs: '262px', sm: '524px' } }}
              />
            </Box>
            {/* CONFIRM PASSWORD INPUT */}
            <Box className={classes.inputFieldBox}>
              <InputField
                onChange={(e) => onInputChange('confirmPassword', e.target.value)}
                error={!!invalidMsg}
                label="Confirm Password"
                type="password"
                placeholder="Re-enter your password"
                helperText={passwordValidationFn}
                size="small"
                sx={{ width: { xs: '262px', sm: '524px' } }}
              />
            </Box>
            {/* ERROR MESSAGE */}
            <Box>{invalidMsg && !updating && <Typography color={color.RED}>{invalidMsg}</Typography>}</Box>
          </>
        ) : (
          <>
            {/* AFTER RESET INSTRUCTIONS */}
            <Box className={classes.inputFieldBox}>
              <Typography>
                We&apos;ve successfully updated your password! Thanks for keeping your profile secure.
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </ActionContainer>
  );
};

export default ResetPasswordView;
